import { useState, useEffect } from "react";
import {
  FaWallet,
  FaArrowTrendUp,
  FaArrowTrendDown,
  FaBuildingColumns,
  FaHandHoldingDollar,
  FaCircleUser,
  FaFilePdf,
} from "react-icons/fa6";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Header } from "../../../../components/Header";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { BaseUrl } from "../../../../utilities/BaseUrl";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/* ============================================
   Dark Theme Chart.js Defaults
   ============================================ */
ChartJS.defaults.color = "#64748B";
ChartJS.defaults.borderColor = "rgba(51, 65, 85, 0.2)";

const TOOLTIP_STYLE = {
  backgroundColor: "rgba(11, 20, 38, 0.95)",
  titleColor: "#F0F4F8",
  bodyColor: "#94A3B8",
  borderColor: "rgba(0, 180, 216, 0.2)",
  borderWidth: 1,
  padding: 14,
  cornerRadius: 10,
  displayColors: true,
  boxPadding: 6,
  titleFont: { size: 13, weight: "600" },
  bodyFont: { size: 12 },
  usePointStyle: true,
};

const GRID_STYLE = {
  color: "rgba(51, 65, 85, 0.2)",
  drawBorder: false,
};

const TICK_STYLE = {
  color: "#64748B",
  font: { size: 11, weight: "500" },
};

/* ============================================
   Skeleton Pulse
   ============================================ */
const SkeletonPulse = ({ className }) => (
  <div className={`animate-pulse rounded-lg bg-slate-700/40 ${className}`} />
);

/* ============================================
   KOMPONEN: StatCard
   ============================================ */
function StatCard({ title, value, icon: Icon, gradient, iconBg, onClick, isLoading }) {
  const isCurrency = true; // Default all finance stats to currency

  return (
    <article
      onClick={onClick}
      className="rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1 group cursor-pointer relative overflow-hidden"
      style={{
        background: "rgba(15, 23, 42, 0.5)",
        border: "1px solid rgba(51, 65, 85, 0.3)",
        backdropFilter: "blur(12px)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(0, 180, 216, 0.15)";
        e.currentTarget.style.boxShadow = "0 8px 30px rgba(0, 0, 0, 0.2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(51, 65, 85, 0.3)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div className="flex items-start justify-between mb-4">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
          style={{
            background: iconBg,
            border: `1px solid ${gradient}`,
            boxShadow: `0 4px 12px ${gradient}20`,
          }}
        >
          <Icon className="text-lg" style={{ color: gradient }} />
        </div>
        
        {/* Decorative Dots */}
        <div className="flex gap-1">
            <div className="h-1.5 w-1.5 rounded-full bg-slate-600" />
            <div className="h-1.5 w-1.5 rounded-full bg-slate-700" />
        </div>
      </div>

      <p className="text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">
        {title}
      </p>

      {isLoading ? (
        <SkeletonPulse className="h-7 w-3/4" />
      ) : (
        <div className="flex items-baseline gap-1">
            <span className="text-xs font-bold text-slate-500">IDR</span>
            <p className="text-xl font-display font-bold text-gray-100 tracking-tight">
            {isCurrency
                ? new Intl.NumberFormat("id-ID", {
                    style: "currency",
                    currency: "IDR",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(value)
                : value.toLocaleString("id-ID")}
            </p>
        </div>
      )}
    </article>
  );
}

/* ============================================
   KOMPONEN: ChartCard
   ============================================ */
function ChartCard({ title, children }) {
  return (
    <article
      className="rounded-2xl p-5 sm:p-6 h-full"
      style={{
        background: "rgba(15, 23, 42, 0.5)",
        border: "1px solid rgba(51, 65, 85, 0.3)",
        backdropFilter: "blur(12px)",
      }}
    >
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-display text-base sm:text-lg font-bold text-gray-100">
          {title}
        </h3>
        <div className="flex gap-4 text-[10px] font-bold uppercase tracking-wider">
            <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-emerald-500 rounded-full"></span> Pendapatan</div>
            <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-rose-500 rounded-full"></span> Pengeluaran</div>
        </div>
      </div>
      {children}
    </article>
  );
}

/* ============================================
   KOMPONEN: ActionCard (Quick Report)
   ============================================ */
function ActionCard({ title, description, buttonText, icon: Icon, onClick }) {
  return (
    <article
      className="rounded-2xl p-6 h-full flex flex-col justify-between relative overflow-hidden group"
      style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        border: "1px solid rgba(51, 65, 85, 0.3)",
      }}
    >
        {/* Background Gradient Glow */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-ocean-600/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>

        <div className="relative z-10">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mb-6 text-ocean-400 group-hover:text-white group-hover:scale-110 transition-all duration-300">
                <Icon size={24} />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
            <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
        </div>

        <button 
            onClick={onClick}
            className="relative z-10 w-full py-3 mt-6 bg-white text-slate-900 rounded-xl font-bold text-sm hover:bg-emerald-50 transition-all active:scale-95 shadow-lg shadow-black/20"
        >
            {buttonText}
        </button>
    </article>
  );
}

/* ============================================
   MAIN COMPONENT: Finance
   ============================================ */
export const FinanceReport = () => {
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [userData, setUserData] = useState(null);

  // Mock Stats Data
  const [stats, setStats] = useState({
    totalSaldo: 12345678,
    pendapatan: 12345678,
    pengeluaran: 0,
    hutang: 0,
    piutang: 0,
  });

  useEffect(() => {
    const t = localStorage.getItem("token-radiustrend");
    const u = localStorage.getItem("user_data-radiustrend");
    if (t && u) {
      setToken(t);
      setUserData(JSON.parse(u));
    } else {
      navigate("/");
    }
  }, [navigate]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  /* ---------- Chart Data ---------- */
  const chartLabels = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun"];

  // Data Dummy dari kode asli
  const rawData = [
    { name: 'Jan', pendapatan: 4000000, pengeluaran: 2400000 },
    { name: 'Feb', pendapatan: 3000000, pengeluaran: 1398000 },
    { name: 'Mar', pendapatan: 5000000, pengeluaran: 3800000 },
    { name: 'Apr', pendapatan: 4780000, pengeluaran: 3908000 },
    { name: 'Mei', pendapatan: 5890000, pengeluaran: 4800000 },
    { name: 'Jun', pendapatan: 7390000, pengeluaran: 3800000 },
  ];

  const financeChartData = {
    labels: rawData.map(d => d.name),
    datasets: [
      {
        label: "Pendapatan",
        data: rawData.map(d => d.pendapatan),
        borderColor: "#10b981", // Emerald-500
        backgroundColor: (context) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 300);
            gradient.addColorStop(0, "rgba(16, 185, 129, 0.2)");
            gradient.addColorStop(1, "rgba(16, 185, 129, 0)");
            return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 7,
        pointBackgroundColor: "#10b981",
        pointBorderColor: "#0B1426",
        pointBorderWidth: 2,
        borderWidth: 2.5,
      },
      {
        label: "Pengeluaran",
        data: rawData.map(d => d.pengeluaran),
        borderColor: "#f43f5e", // Rose-500
        backgroundColor: (context) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 300);
            gradient.addColorStop(0, "rgba(244, 63, 94, 0.2)");
            gradient.addColorStop(1, "rgba(244, 63, 94, 0)");
            return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 7,
        pointBackgroundColor: "#f43f5e",
        pointBorderColor: "#0B1426",
        pointBorderWidth: 2,
        borderWidth: 2.5,
      },
    ],
  };

  /* ---------- Chart Options ---------- */
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false }, // Legend is handled manually in ChartCard header
      tooltip: {
        ...TOOLTIP_STYLE,
        callbacks: {
          label: (ctx) => {
            const val = ctx.parsed.y;
            const label = ctx.dataset.label;
            return `${label}: ${formatCurrency(val)}`;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: TICK_STYLE },
      y: {
        grid: GRID_STYLE,
        ticks: {
          ...TICK_STYLE,
          callback: (value) => {
             if (value >= 1000000) return (value/1000000) + 'jt';
             if (value >= 1000) return (value/1000) + 'rb';
             return value;
          },
        },
      },
    },
  };

  /* ---------- Stat Cards Config ---------- */
  const statCards = [
    {
      title: "Total Saldo",
      key: "totalSaldo",
      icon: FaWallet,
      gradient: "#3b82f6", // Blue-500
      iconBg: "rgba(59, 130, 246, 0.12)",
      onClick: () => navigate('/dashboard/finance/transaction'),
    },
    {
      title: "Pendapatan",
      key: "pendapatan",
      icon: FaArrowTrendUp,
      gradient: "#10b981", // Emerald-500
      iconBg: "rgba(16, 185, 129, 0.12)",
      onClick: () => navigate('/dashboard/finance/transaction'),
    },
    {
      title: "Pengeluaran",
      key: "pengeluaran",
      icon: FaArrowTrendDown,
      gradient: "#f43f5e", // Rose-500
      iconBg: "rgba(244, 63, 94, 0.12)",
      onClick: () => navigate('/dashboard/finance/transaction'),
    },
    {
      title: "Hutang",
      key: "hutang",
      icon: FaBuildingColumns,
      gradient: "#f59e0b", // Amber-500
      iconBg: "rgba(245, 158, 11, 0.12)",
      onClick: () => navigate('/dashboard/finance/hutang'),
    },
    {
      title: "Piutang",
      key: "piutang",
      icon: FaHandHoldingDollar,
      gradient: "#a855f7", // Purple-500
      iconBg: "rgba(168, 85, 247, 0.12)",
      onClick: () => navigate('/dashboard/finance/piutang'),
    },
  ];

  /* ---------- Fetch Stats Simulation ---------- */
  useEffect(() => {
    const fetchStats = async () => {
      setIsLoading(true);
      try {
        // Simulate API Call
        setTimeout(() => {
            setStats({
                totalSaldo: 12345678,
                pendapatan: 26560000,
                pengeluaran: 14214322,
                hutang: 5000000,
                piutang: 2500000,
            });
            setIsLoading(false);
        }, 800);
      } catch (error) {
        console.error("Error fetching stats:", error);
        setIsLoading(false);
      }
    };
    fetchStats();
  }, []);

  return (
    <>
      <Helmet>
        <title>Finance | Danela Net</title>
        <meta
          name="description"
          content="Halaman Keuangan Danela Net — monitoring saldo, pendapatan, pengeluaran, hutang, dan piutang."
        />
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex flex-col">
            <h2 className="text-lg md:text-xl font-display font-bold text-gray-100 tracking-tight">
              Keuangan
            </h2>
            <p className="text-xs text-slate-500 hidden md:block mt-0.5 uppercase tracking-[0.2em]">
              Ringkasan Arus Kas
            </p>
          </div>

          {/* Profile Section (Sesuai Referensi) */}
          <div className="flex items-center gap-3 pl-4 border-l border-slate-700/50">
            <div className="hidden sm:flex flex-col text-right">
              <span className="text-sm font-semibold text-gray-200 leading-none mb-1">
                {userData?.name || "Admin"}
              </span>
              <span className="text-[10px] font-semibold text-ocean-400 uppercase tracking-wider leading-none">
                {userData?.role?.name || "Finance Manager"}
              </span>
            </div>
            <div className="relative group cursor-pointer">
              {userData?.image ? (
                <img
                  src={`${BaseUrl}/storage/${userData.image}`}
                  alt="Profile"
                  className="w-10 h-10 rounded-xl object-cover ring-2 ring-slate-700/50 group-hover:ring-ocean-600/50 transition-all"
                />
              ) : (
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-400 ring-2 ring-slate-700/50 group-hover:ring-ocean-600/50 transition-all"
                  style={{
                    background: "linear-gradient(135deg, rgba(0,180,216,0.15), rgba(0,119,182,0.08))",
                    border: "1px solid rgba(0,180,216,0.15)",
                  }}
                >
                  <FaCircleUser className="text-lg" />
                </div>
              )}
              <span
                className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2"
                style={{
                  background: "#34d399",
                  borderColor: "#0B1426",
                }}
              />
            </div>
          </div>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-6">
          {/* Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4">
            {statCards.map((card) => (
              <StatCard
                key={card.key}
                title={card.title}
                value={stats[card.key]}
                icon={card.icon}
                gradient={card.gradient}
                iconBg={card.iconBg}
                onClick={card.onClick}
                isLoading={isLoading}
              />
            ))}
          </div>

          {/* Charts & Actions Section */}
          <div className="flex gap-6 w-full">
            
            {/* Chart Card (Span 2 Cols) */}
            <ChartCard title="Statistik Bulanan" className="">
              <div className="h-[300px] sm:h-[340px] w-full">
                <Line data={financeChartData} options={chartOptions} />
              </div>
            </ChartCard>

            {/* Action Card (Quick Report) */}
            <div className="w-full">
                <ActionCard 
                    title="Laporan Cepat"
                    description="Analisis data keuangan Anda secara otomatis dan unduh laporan bulanan dalam satu klik."
                    buttonText="Unduh PDF"
                    icon={FaFilePdf}
                    onClick={() => console.log("Download PDF Clicked")}
                />
            </div>
          </div>
        </main>
      </DashboardLayout>
    </>
  );
};